/**
 * Project View Details page — route: /projects/:id
 * This is the full-page view when you click "View Details" on a project (e.g. from Projects list).
 * All layout/order changes for that page must be made in this file.
 */
import React, { useState } from 'react';
import { useRoute } from 'wouter';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge } from '../components/ui/badge';
import StatusBadge from '../components/shared/status-badge';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, 
  Edit, 
  Trash2, 
  CalendarIcon,
  DollarSign,
  Building,
  User,
  MapPin,
  Clock,
  Save,
  X,
  Plus,
  FileText,
  AlertCircle,
  CheckCircle2,
  ListTodo,
  Loader2,
  Sparkles
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Sidebar from '../components/layout/sidebar';
import MobileSidebar from '../components/layout/mobile-sidebar';
import TopNav from '../components/layout/top-nav';
import { apiRequest } from '../lib/queryClient';
import { useToast } from '../hooks/use-toast';
import { Link } from 'wouter';
import { formatCurrency } from '../lib/utils';
import ErrorBoundary from '../components/error-boundary';
import { formatPhone } from '../lib/validation';

// Helper: parse project dates consistently (avoids timezone shifting)
const parseProjectDate = (raw: string | Date | null | undefined): Date | null => {
  if (raw == null) return null;
  if (raw instanceof Date) return isNaN(raw.getTime()) ? null : raw;
  const str = String(raw).trim().slice(0, 10);
  const dateOnlyMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const [, y, m, d] = dateOnlyMatch;
    return new Date(Number(y), Number(m) - 1, Number(d), 12, 0, 0);
  }
  const d = new Date(raw);
  return !isNaN(d.getTime()) ? d : null;
};

// Helper function to format date using normalized parser
const formatDate = (dateString?: string | Date | null) => {
  const date = parseProjectDate(dateString ?? null);
  if (!date) return 'N/A';
  try {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch {
    return 'Invalid Date';
  }
};

// Helper function to format hours to hours and minutes
const formatHoursMinutes = (hours: number): string => {
  if (!hours || hours === 0) return '0 min';
  
  const totalMinutes = Math.round(hours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  
  if (h === 0) {
    return `${m} min`;
  } else if (m === 0) {
    return `${h} hr${h > 1 ? 's' : ''}`;
  } else {
    return `${h} hr${h > 1 ? 's' : ''} ${m} min`;
  }
};

// Helper function to get status badge styling (standardized)
const getStatusBadge = (status: string) => {
  return <StatusBadge status={status} type="project" />;
};

export default function ProjectDetailPage() {
  const [, params] = useRoute('/projects/:id');
  const projectId = (params as any)?.id || '';
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Dialog states
  const [isScopeDialogOpen, setIsScopeDialogOpen] = useState(false);
  const [isTimelineDialogOpen, setIsTimelineDialogOpen] = useState(false);
  const [isNotesDialogOpen, setIsNotesDialogOpen] = useState(false);
  const [isAddTaskDialogOpen, setIsAddTaskDialogOpen] = useState(false);
  const [isEditTaskDialogOpen, setIsEditTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<any>(null);

  // Form values
  const [editDescription, setEditDescription] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [newTaskName, setNewTaskName] = useState('');
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [editTaskName, setEditTaskName] = useState('');
  const [editTaskDescription, setEditTaskDescription] = useState('');
  const [editTaskStatus, setEditTaskStatus] = useState('not_started');

  // Individual notes state
  const [isAddingProjectNote, setIsAddingProjectNote] = useState(false);
  const [newProjectNoteText, setNewProjectNoteText] = useState('');
  const [projectNoteType, setProjectNoteType] = useState('general');

  // Add error boundary for React errors
  React.useEffect(() => {
    const handleError = (error: ErrorEvent) => {
      console.error('ProjectDetailPage Error:', error);
      toast({
        title: "Error",
        description: "An error occurred while loading the project.",
        variant: "destructive"
      });
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, [toast]);

  // Fetch project details
  const { data: project, isLoading, error } = useQuery({
    queryKey: ['/api/protected/projects', projectId],
    queryFn: async () => {
      try {
        console.log('Fetching project details for ID:', projectId);
        const response = await apiRequest("GET", `/api/protected/projects?id=${projectId}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        console.log('Project data received:', data);
        return data;
      } catch (err) {
        console.error('Error fetching project:', err);
        throw err;
      }
    },
    enabled: !!projectId,
    retry: 1,
    retryDelay: 1000
  });

  // Update project mutation
  const updateProjectMutation = useMutation({
    mutationFn: async (updates: { description?: string; notes?: string; startDate?: string; endDate?: string; dialogType?: 'scope' | 'timeline' | 'notes' }) => {
      const { dialogType, ...updateData } = updates;
      const response = await apiRequest("PATCH", `/api/protected/projects?id=${projectId}`, updateData);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update project');
      }
      return { data: await response.json(), dialogType };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['/api/protected/projects', projectId] });
      
      // Close the appropriate dialog
      if (result.dialogType === 'scope') setIsScopeDialogOpen(false);
      if (result.dialogType === 'timeline') setIsTimelineDialogOpen(false);
      if (result.dialogType === 'notes') setIsNotesDialogOpen(false);
      
      toast({
        title: "Success",
        description: "Project updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update project",
        variant: "destructive",
      });
    }
  });

  // Fetch project notes (individual log entries)
  const { data: projectNotes = [], isLoading: isLoadingProjectNotes } = useQuery({
    queryKey: ['/api/protected/project-notes', projectId],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/protected/project-notes?projectId=${projectId}`);
      if (!response.ok) throw new Error('Failed to fetch project notes');
      return response.json();
    },
    enabled: !!projectId
  });

  // Fetch timeclock entries for this project
  const { data: timeclockEntries = [], isLoading: isLoadingTimeclock } = useQuery({
    queryKey: ['/api/timeclock/project', projectId],
    queryFn: async () => {
      try {
        const response = await apiRequest("GET", `/api/timeclock/project?projectId=${projectId}`);
        if (!response.ok) {
          // If timeclock is not available, return empty array
          return [];
        }
        const entries = await response.json();
        // Filter to only completed entries (have clock_out_time) and transform for display
        return entries
          .filter((entry: any) => {
            // For single-row schema: must have clock_out_time
            if (entry.clock_out_time || entry.total_hours) {
              return true;
            }
            return false;
          })
          .map((entry: any) => {
            // Parse timestamps (may be BIGINT or ISO string)
            const clockInMs = entry.clock_in_time 
              ? (typeof entry.clock_in_time === 'string' ? parseInt(entry.clock_in_time, 10) : entry.clock_in_time)
              : null;
            const clockOutMs = entry.clock_out_time
              ? (typeof entry.clock_out_time === 'string' ? parseInt(entry.clock_out_time, 10) : entry.clock_out_time)
              : null;
            
            const clockInDate = clockInMs ? new Date(clockInMs) : null;
            const clockOutDate = clockOutMs ? new Date(clockOutMs) : null;
            
            // Calculate hours if not provided
            let hours = entry.total_hours;
            if (!hours && clockInDate && clockOutDate) {
              hours = ((clockOutDate.getTime() - clockInDate.getTime()) / (1000 * 60 * 60)).toFixed(2);
            }
            
            return {
              id: `timeclock-${entry.id}`,
              type: 'timeclock',
              employeeName: entry.employee_name || entry.employeeName,
              clockInTime: clockInDate,
              clockOutTime: clockOutDate,
              hours: hours ? parseFloat(hours.toString()) : 0,
              activity: entry.task_name || entry.notes || entry.jobType || 'Work',
              location: entry.location,
              createdAt: clockInDate || new Date()
            };
          });
      } catch (error) {
        console.error('Error fetching timeclock entries:', error);
        return [];
      }
    },
    enabled: !!projectId,
    refetchInterval: 60000 // Refresh every minute
  });

  // Fetch project tasks
  const { data: tasksData, isLoading: isLoadingTasks, refetch: refetchTasks } = useQuery({
    queryKey: ['/api/tasks', projectId],
    queryFn: async () => {
      try {
        const response = await apiRequest("GET", `/api/tasks?projectId=${projectId}`);
        if (!response.ok) {
          return [];
        }
        const data = await response.json();
        return data.tasks || [];
      } catch (error) {
        console.error('Error fetching tasks:', error);
        return [];
      }
    },
    enabled: !!projectId
  });

  const projectTasks = tasksData || [];

  // Add project note mutation
  const addProjectNoteMutation = useMutation({
    mutationFn: async (noteData: { projectId: string; userName: string; noteText: string; noteType: string }) => {
      const response = await apiRequest("POST", '/api/protected/project-notes', noteData);
      if (!response.ok) throw new Error('Failed to add project note');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/protected/project-notes', projectId] });
      setIsAddingProjectNote(false);
      setNewProjectNoteText('');
      setProjectNoteType('general');
      toast({
        title: "Success",
        description: "Project note added successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add project note",
        variant: "destructive",
      });
    }
  });

  // Task mutations
  const addTaskMutation = useMutation({
    mutationFn: async (taskData: { projectId: string; taskName: string; taskDescription: string }) => {
      const response = await apiRequest("POST", '/api/tasks', taskData);
      if (!response.ok) throw new Error('Failed to create task');
      return response.json();
    },
    onSuccess: () => {
      refetchTasks();
      setIsAddTaskDialogOpen(false);
      setNewTaskName('');
      setNewTaskDescription('');
      toast({
        title: "Success",
        description: "Task created successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create task",
        variant: "destructive",
      });
    }
  });

  const updateTaskMutation = useMutation({
    mutationFn: async ({ taskId, taskName, taskDescription, status }: { taskId: number; taskName: string; taskDescription: string; status: string }) => {
      const response = await apiRequest("PUT", `/api/tasks?id=${taskId}`, {
        taskName,
        taskDescription,
        status
      });
      if (!response.ok) throw new Error('Failed to update task');
      return response.json();
    },
    onSuccess: () => {
      refetchTasks();
      setIsEditTaskDialogOpen(false);
      setEditingTask(null);
      toast({
        title: "Success",
        description: "Task updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update task",
        variant: "destructive",
      });
    }
  });

  const deleteTaskMutation = useMutation({
    mutationFn: async (taskId: number) => {
      const response = await apiRequest("DELETE", `/api/tasks?id=${taskId}`);
      if (!response.ok) throw new Error('Failed to delete task');
      return response.json();
    },
    onSuccess: () => {
      refetchTasks();
      toast({
        title: "Success",
        description: "Task deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete task",
        variant: "destructive",
      });
    }
  });

  const generateTasksMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", '/api/tasks/generate', {
        projectId: projectId,
        projectTitle: project?.title,
        projectDescription: project?.description
      });
      if (!response.ok) throw new Error('Failed to generate tasks');
      return response.json();
    },
    onSuccess: (data) => {
      refetchTasks();
      toast({
        title: "Success",
        description: `Generated ${data.tasks?.length || 0} tasks with AI`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to generate tasks",
        variant: "destructive",
      });
    }
  });

  // Handle Scope edit
  const handleScopeEdit = () => {
    setEditDescription(project?.description || '');
    setIsScopeDialogOpen(true);
  };

  const handleScopeSave = () => {
    updateProjectMutation.mutate({ description: editDescription, dialogType: 'scope' });
  };

  // Handle Timeline edit
  const handleTimelineEdit = () => {
    // Convert UNIX timestamps to YYYY-MM-DD format
    const startDate = project?.startDate ? new Date(typeof project.startDate === 'number' ? project.startDate * 1000 : project.startDate).toISOString().split('T')[0] : '';
    const endDate = project?.endDate ? new Date(typeof project.endDate === 'number' ? project.endDate * 1000 : project.endDate).toISOString().split('T')[0] : '';
    setEditStartDate(startDate);
    setEditEndDate(endDate);
    setIsTimelineDialogOpen(true);
  };

  const handleTimelineSave = () => {
    updateProjectMutation.mutate({ 
      startDate: editStartDate || undefined, 
      endDate: editEndDate || undefined,
      dialogType: 'timeline'
    });
  };

  // Handle Notes edit
  const handleNotesEdit = () => {
    setEditNotes(project?.notes || '');
    setIsNotesDialogOpen(true);
  };

  const handleNotesSave = () => {
    updateProjectMutation.mutate({ notes: editNotes, dialogType: 'notes' });
  };

  // Handle adding individual project note
  const handleAddProjectNote = () => {
    if (!newProjectNoteText.trim()) {
      toast({
        title: "Error",
        description: "Please enter a note",
        variant: "destructive",
      });
      return;
    }

    // Get user name from localStorage or use default
    const userData = localStorage.getItem('user');
    const userName = userData ? JSON.parse(userData).username || JSON.parse(userData).email || 'User' : 'User';

    addProjectNoteMutation.mutate({
      projectId: projectId,
      userName: userName,
      noteText: newProjectNoteText,
      noteType: projectNoteType
    });
  };

  // Task handlers
  const handleAddTask = () => {
    if (!newTaskName.trim()) {
      toast({
        title: "Error",
        description: "Please enter a task name",
        variant: "destructive",
      });
      return;
    }

    addTaskMutation.mutate({
      projectId: projectId,
      taskName: newTaskName,
      taskDescription: newTaskDescription
    });
  };

  const handleEditTask = (task: any) => {
    setEditingTask(task);
    setEditTaskName(task.taskName);
    setEditTaskDescription(task.taskDescription || '');
    setEditTaskStatus(task.status);
    setIsEditTaskDialogOpen(true);
  };

  const handleUpdateTask = () => {
    if (!editTaskName.trim()) {
      toast({
        title: "Error",
        description: "Please enter a task name",
        variant: "destructive",
      });
      return;
    }

    updateTaskMutation.mutate({
      taskId: editingTask.id,
      taskName: editTaskName,
      taskDescription: editTaskDescription,
      status: editTaskStatus
    });
  };

  const handleDeleteTask = (taskId: number) => {
    if (confirm('Are you sure you want to delete this task?')) {
      deleteTaskMutation.mutate(taskId);
    }
  };

  const handleGenerateTasks = () => {
    if (!project?.description || project.description.trim().length < 10) {
      toast({
        title: "Error",
        description: "Please add a project description first to generate tasks with AI",
        variant: "destructive",
      });
      return;
    }

    if (confirm('This will generate tasks using AI based on your project description. Continue?')) {
      generateTasksMutation.mutate();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500/10 text-green-500 border-green-500/30';
      case 'in_progress':
        return 'bg-blue-500/10 text-blue-500 border-blue-500/30';
      default:
        return 'bg-slate-500/10 text-slate-400 border-slate-500/30';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'in_progress':
        return 'In Progress';
      default:
        return 'Not Started';
    }
  };

  if (isLoading) {
    return (
      <div className="remodra-layout">
        <div className="hidden md:block">
          <Sidebar />
        </div>
        <MobileSidebar />
        <div className="remodra-main-mobile md:ml-64">
          <TopNav />
          <div className="remodra-content flex justify-center">
            <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 pt-24 pb-8">
              <div className="remodra-card p-8 border-green-500/30">
                <div className="flex flex-col items-center justify-center min-h-[400px]">
                  <Loader2 className="h-12 w-12 animate-spin text-green-500 mb-4" />
                  <span className="text-lg text-slate-600 dark:text-slate-300 font-medium">Loading project...</span>
                  <span className="text-sm text-slate-400 dark:text-slate-500 mt-2">Please wait</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="remodra-layout">
        <div className="hidden md:block">
          <Sidebar />
        </div>
        <MobileSidebar />
        <div className="remodra-main-mobile md:ml-64">
          <TopNav />
          <div className="remodra-content flex justify-center">
            <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 pt-24 pb-8">
              <div className="remodra-card p-8 border-red-500/30">
                <div className="mb-6">
                  <Link href="/projects">
                    <Button variant="ghost" className="mb-4 remodra-button-outline">
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Back to Projects
                    </Button>
                  </Link>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                      <AlertCircle className="h-6 w-6 text-red-500" />
                    </div>
                    <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-200">Project Not Found</h1>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mt-4">
                    <p className="text-red-800 dark:text-red-200 font-medium">
                      The project you're looking for doesn't exist or you don't have permission to view it.
                    </p>
                  </div>
                  <div className="mt-6">
                    <Link href="/projects">
                      <Button className="remodra-button">Go to Projects</Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="remodra-layout">
        <div className="hidden md:block">
          <Sidebar />
        </div>
        <MobileSidebar />
        <div className="remodra-main-mobile md:ml-64">
          <TopNav />
          <div className="remodra-content flex justify-center">
            <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 pt-24 pb-8">
            {/* Header */}
            <div className="mb-8">
              <Link href="/projects">
                <Button variant="ghost" className="mb-4 text-slate-700 dark:text-slate-300 hover:text-green-500 dark:hover:text-green-500 hover:bg-slate-100 dark:hover:bg-slate-700/50">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Projects
                </Button>
              </Link>
              <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-200 mb-2">
                {project?.title || 'Untitled Project'}
              </h1>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className="inline-flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                  <Building className="h-3.5 w-3.5" />
                  {project?.serviceType ? project.serviceType.charAt(0).toUpperCase() + project.serviceType.slice(1) : 'N/A'}
                </span>
                <span className="text-slate-400">·</span>
                {getStatusBadge(project?.status || '')}
              </div>
            </div>

            {/* Timeline - simple section */}
            <div className="flex flex-wrap items-center justify-between gap-4 py-4 mb-6 border-b border-slate-200 dark:border-slate-700">
              <div className="flex gap-6">
                <div>
                  <span className="text-xs text-slate-500 dark:text-slate-400 block">Started</span>
                  <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{formatDate(project?.startDate) || 'Not set'}</span>
                </div>
                <div>
                  <span className="text-xs text-slate-500 dark:text-slate-400 block">Est. Completion</span>
                  <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{formatDate(project?.endDate) || 'Not set'}</span>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={handleTimelineEdit} className="remodra-button-outline-sm">
                <Edit className="h-3 w-3 mr-1" />
                Edit Timeline
              </Button>
            </div>

            {/* Client + Agent */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
              {project.client && (
                <section>
                  <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-3 flex items-center gap-2">
                    <User className="h-4 w-4 text-green-500" />
                    Client
                  </h2>
                  <div className="space-y-2 text-sm">
                    <p className="font-medium text-slate-800 dark:text-slate-200">
                      {project.client.firstName || 'N/A'} {project.client.lastName || ''}
                    </p>
                    <p className="text-slate-600 dark:text-slate-400">{project.client.email || 'N/A'}</p>
                    {project.client.phone && <p className="text-slate-600 dark:text-slate-400">{formatPhone(project.client.phone)}</p>}
                    {(() => {
                      const addr = project.client.address ?? project.client.street_address;
                      const city = project.client.city;
                      const state = project.client.state;
                      const zip = project.client.zip;
                      const hasAddress = addr || city || state || zip;
                      return hasAddress ? (
                        <p className="text-slate-600 dark:text-slate-400 flex items-start gap-1">
                          <MapPin className="h-3 w-3 mt-0.5 shrink-0" />
                          {[addr, city, state, zip].filter(Boolean).join(', ')}
                        </p>
                      ) : null;
                    })()}
                  </div>
                </section>
              )}
              {project.agent && (
                <section>
                  <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-3 flex items-center gap-2">
                    <User className="h-4 w-4 text-green-500" />
                    Assigned Agent
                  </h2>
                  <div className="space-y-2 text-sm">
                    <p className="font-medium text-slate-800 dark:text-slate-200">
                      {project.agent.firstName || 'N/A'} {project.agent.lastName || ''}
                    </p>
                    <p className="text-slate-600 dark:text-slate-400">{project.agent.email || 'N/A'}</p>
                    {project.agent.phone && <p className="text-slate-600 dark:text-slate-400">{formatPhone(project.agent.phone)}</p>}
                  </div>
                </section>
              )}
            </div>

            {/* Scope */}
            <section className="mb-8">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-green-500" />
                  Project Scope
                </h2>
                <Button variant="outline" size="sm" onClick={handleScopeEdit} className="remodra-button-outline-sm">
                  <Edit className="h-3 w-3 mr-1" />
                  Edit
                </Button>
              </div>
              <p className="text-slate-600 dark:text-slate-300 whitespace-pre-wrap leading-relaxed">
                {project?.description || 'No scope available.'}
              </p>
            </section>

            {/* Summary */}
            <section className="mb-8">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-green-500" />
                  Summary
                </h2>
                <Button variant="outline" size="sm" onClick={handleNotesEdit} className="remodra-button-outline-sm">
                  <Edit className="h-3 w-3 mr-1" />
                  Edit
                </Button>
              </div>
              <p className="text-slate-600 dark:text-slate-300 whitespace-pre-wrap text-sm leading-relaxed min-h-[60px]">
                {project?.notes || 'No summary note yet. Click "Edit" to add one.'}
              </p>
            </section>

            {/* Project Tasks */}
            <section className="mb-8">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                    <ListTodo className="h-4 w-4 text-green-500" />
                    Project Tasks
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Tasks workers can clock in/out to</p>
                </div>
                <div className="flex gap-2">
                  {projectTasks.length === 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleGenerateTasks}
                      disabled={generateTasksMutation.isPending}
                      className="remodra-button-outline-sm"
                    >
                      {generateTasksMutation.isPending ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4 mr-2" />
                          Generate with AI
                        </>
                      )}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsAddTaskDialogOpen(true)}
                    className="remodra-button-outline-sm"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Task
                  </Button>
                </div>
              </div>
              <div className="border border-slate-200 dark:border-slate-600 rounded-lg p-4">
                {isLoadingTasks ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-green-500" />
                  </div>
                ) : projectTasks.length === 0 ? (
                  <div className="text-center py-8">
                    <ListTodo className="h-12 w-12 mx-auto mb-3 text-slate-400 dark:text-slate-500" />
                    <p className="text-slate-500 dark:text-slate-400 text-sm">No tasks yet</p>
                    <p className="text-slate-400 dark:text-slate-500 text-xs mt-1">Generate tasks with AI or add manually</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {projectTasks.map((task: any) => (
                      <div 
                        key={task.id} 
                        className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium text-slate-800 dark:text-slate-200 text-sm">{task.taskName}</h4>
                              <Badge className={`text-xs ${getStatusColor(task.status)}`}>
                                {task.status === 'completed' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                                {getStatusLabel(task.status)}
                              </Badge>
                              {task.isAiGenerated && (
                                <Badge variant="outline" className="text-xs text-purple-400 border-purple-400/30">
                                  <Sparkles className="h-3 w-3 mr-1" />
                                  AI
                                </Badge>
                              )}
                            </div>
                            {task.taskDescription && (
                              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{task.taskDescription}</p>
                            )}
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500 dark:text-slate-400">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatHoursMinutes(task.totalLoggedHours || 0)} logged
                              </span>
                              {task.workerCount > 0 && (
                                <span className="flex items-center gap-1">
                                  <User className="h-3 w-3" />
                                  {task.workerCount} worker{task.workerCount > 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditTask(task)}
                              className="h-8 w-8 p-0"
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteTask(task.id)}
                              className="h-8 w-8 p-0 text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>

            {/* Activity Log */}
            <section>
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-green-500" />
                    Activity Log
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Track progress, issues, and milestones</p>
                </div>
                <Button
                  size="sm"
                  className="remodra-button"
                  onClick={() => setIsAddingProjectNote(true)}
                  disabled={isAddingProjectNote}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Note
                </Button>
              </div>
              <div className="border border-slate-200 dark:border-slate-600 rounded-lg p-4">
                {/* Add Note Form */}
                {isAddingProjectNote && (
                  <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 block">
                          Note Type
                        </label>
                        <select
                          value={projectNoteType}
                          onChange={(e) => setProjectNoteType(e.target.value)}
                          className="remodra-input w-full"
                        >
                          <option value="general">📝 General</option>
                          <option value="progress">🚀 Progress Update</option>
                          <option value="issue">⚠️ Issue/Problem</option>
                          <option value="milestone">🎯 Milestone</option>
                          <option value="material">📦 Material/Supply</option>
                          <option value="worker">👷 Worker/Team Note</option>
                          <option value="client">👤 Client Communication</option>
                          <option value="inspection">🔍 Inspection</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 block">
                          Note Details
                        </label>
                        <Textarea
                          value={newProjectNoteText}
                          onChange={(e) => setNewProjectNoteText(e.target.value)}
                          placeholder="Example: Completed foundation work. Concrete poured and level verified..."
                          rows={4}
                          className="remodra-input"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          onClick={handleAddProjectNote}
                          disabled={addProjectNoteMutation.isPending || !newProjectNoteText.trim()}
                          className="remodra-button"
                        >
                          {addProjectNoteMutation.isPending ? 'Saving...' : 'Save Note'}
                        </Button>
                        <Button 
                          variant="outline"
                          onClick={() => {
                            setIsAddingProjectNote(false);
                            setNewProjectNoteText('');
                            setProjectNoteType('general');
                          }}
                          disabled={addProjectNoteMutation.isPending}
                          className="remodra-button-outline"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Combined Activity List (Notes + Timeclock) */}
                <div className="space-y-3">
                  {(isLoadingProjectNotes || isLoadingTimeclock) ? (
                    <div className="text-center py-8 text-slate-400">
                      Loading activities...
                    </div>
                  ) : (() => {
                        // Combine and sort all activities by date (newest first)
                        const allActivities: any[] = [
                          ...projectNotes.map((note: any) => ({
                            ...note,
                            sortDate: new Date(note.createdAt).getTime()
                          })),
                          ...timeclockEntries.map((entry: any) => ({
                            ...entry,
                            sortDate: entry.createdAt ? new Date(entry.createdAt).getTime() : 0
                          }))
                        ].sort((a, b) => b.sortDate - a.sortDate);

                    if (allActivities.length === 0) {
                      return (
                        <div className="text-center py-8 border border-dashed border-slate-300 dark:border-slate-600 rounded-lg">
                          <FileText className="h-12 w-12 text-slate-400 mx-auto mb-3" />
                          <p className="text-slate-600 dark:text-slate-400 mb-2">No activity yet</p>
                          <p className="text-sm text-slate-500 dark:text-slate-500">
                            Click "Add Note" to track progress, issues, or milestones
                          </p>
                        </div>
                      );
                    }

                    return allActivities.map((activity: any) => {
                      // Timeclock entry
                      if (activity.type === 'timeclock') {
                        return (
                          <div 
                            key={activity.id}
                            className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg"
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                  {activity.clockInTime?.toLocaleString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    hour12: true
                                  }) || 'N/A'}
                                </span>
                              </div>
                              <Badge variant="outline" className="text-xs bg-blue-100 dark:bg-blue-900/50 border-blue-300 dark:border-blue-700">
                                ⏱️ {formatHoursMinutes(activity.hours)}
                              </Badge>
                            </div>
                            <div className="space-y-1">
                              <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                                👷 {activity.employeeName}
                              </p>
                              <p className="text-sm text-slate-600 dark:text-slate-400">
                                {activity.clockInTime && activity.clockOutTime ? (
                                  <>
                                    <span className="text-green-600 dark:text-green-400">In:</span> {activity.clockInTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                    {' → '}
                                    <span className="text-red-600 dark:text-red-400">Out:</span> {activity.clockOutTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                  </>
                                ) : (
                                  <span className="text-yellow-600 dark:text-yellow-400">Currently clocked in</span>
                                )}
                              </p>
                              {activity.activity && (
                                <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                                  Activity: {activity.activity}
                                </p>
                              )}
                              {activity.location && (
                                <p className="text-xs text-slate-500 dark:text-slate-500">
                                  📍 {activity.location}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      }

                      // Project note entry
                      const noteTypeEmoji = {
                        general: '📝',
                        progress: '🚀',
                        issue: '⚠️',
                        milestone: '🎯',
                        material: '📦',
                        worker: '👷',
                        client: '👤',
                        inspection: '🔍'
                      }[activity.noteType] || '📝';

                      return (
                        <div 
                          key={activity.id}
                          className="p-4 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">{noteTypeEmoji}</span>
                              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                {new Date(activity.createdAt).toLocaleString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  hour12: true
                                })}
                              </span>
                            </div>
                            <Badge variant="outline" className="text-xs">
                              {activity.userName}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-800 dark:text-slate-200 whitespace-pre-wrap">
                            {activity.noteText}
                          </p>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Scope Edit Dialog */}
    <Dialog open={isScopeDialogOpen} onOpenChange={setIsScopeDialogOpen}>
      <DialogContent className="bg-slate-800 border-slate-600 text-slate-100">
        <DialogHeader>
          <DialogTitle className="text-green-500">Edit Scope</DialogTitle>
          <DialogDescription className="text-slate-400">
            Update the project scope/description
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="scope" className="text-slate-300">Scope Description</Label>
            <Textarea
              id="scope"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              placeholder="Enter project scope..."
              rows={6}
              className="remodra-input mt-2"
            />
          </div>
        </div>
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => setIsScopeDialogOpen(false)}
            className="remodra-button-outline"
          >
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button 
            onClick={handleScopeSave}
            disabled={updateProjectMutation.isPending}
            className="remodra-button"
          >
            <Save className="h-4 w-4 mr-2" />
            {updateProjectMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Timeline Edit Dialog */}
    <Dialog open={isTimelineDialogOpen} onOpenChange={setIsTimelineDialogOpen}>
      <DialogContent className="bg-slate-800 border-slate-600 text-slate-100">
        <DialogHeader>
          <DialogTitle className="text-green-500">Edit Timeline</DialogTitle>
          <DialogDescription className="text-slate-400">
            Update the project start and end dates
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="startDate" className="text-slate-300">Start Date</Label>
            <Input
              id="startDate"
              type="date"
              value={editStartDate}
              onChange={(e) => setEditStartDate(e.target.value)}
              className="remodra-input mt-2"
            />
          </div>
          <div>
            <Label htmlFor="endDate" className="text-slate-300">End Date</Label>
            <Input
              id="endDate"
              type="date"
              value={editEndDate}
              onChange={(e) => setEditEndDate(e.target.value)}
              className="remodra-input mt-2"
            />
          </div>
        </div>
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => setIsTimelineDialogOpen(false)}
            className="remodra-button-outline"
          >
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button 
            onClick={handleTimelineSave}
            disabled={updateProjectMutation.isPending}
            className="remodra-button"
          >
            <Save className="h-4 w-4 mr-2" />
            {updateProjectMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Notes Edit Dialog */}
    <Dialog open={isNotesDialogOpen} onOpenChange={setIsNotesDialogOpen}>
      <DialogContent className="bg-slate-800 border-slate-600 text-slate-100">
        <DialogHeader>
          <DialogTitle className="text-green-500">Edit Project Summary</DialogTitle>
          <DialogDescription className="text-slate-400">
            Update the project summary note (for general overview, not activity tracking)
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="notes" className="text-slate-300">Summary Note</Label>
            <Textarea
              id="notes"
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              placeholder="Enter project notes..."
              rows={6}
              className="remodra-input mt-2"
            />
          </div>
        </div>
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => setIsNotesDialogOpen(false)}
            className="remodra-button-outline"
          >
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button 
            onClick={handleNotesSave}
            disabled={updateProjectMutation.isPending}
            className="remodra-button"
          >
            <Save className="h-4 w-4 mr-2" />
            {updateProjectMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Add Task Dialog */}
    <Dialog open={isAddTaskDialogOpen} onOpenChange={setIsAddTaskDialogOpen}>
      <DialogContent className="bg-slate-800 border-slate-600 text-slate-100">
        <DialogHeader>
          <DialogTitle className="text-green-500">Add New Task</DialogTitle>
          <DialogDescription className="text-slate-400">
            Create a task that workers can clock in/out to
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="taskName" className="text-slate-300">Task Name *</Label>
            <Input
              id="taskName"
              value={newTaskName}
              onChange={(e) => setNewTaskName(e.target.value)}
              placeholder="e.g., Install Roofing Shingles"
              className="remodra-input mt-2"
            />
          </div>
          <div>
            <Label htmlFor="taskDescription" className="text-slate-300">Description (Optional)</Label>
            <Textarea
              id="taskDescription"
              value={newTaskDescription}
              onChange={(e) => setNewTaskDescription(e.target.value)}
              placeholder="Brief description of the task..."
              rows={3}
              className="remodra-input mt-2"
            />
          </div>
        </div>
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => {
              setIsAddTaskDialogOpen(false);
              setNewTaskName('');
              setNewTaskDescription('');
            }}
            className="remodra-button-outline"
          >
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button 
            onClick={handleAddTask}
            disabled={addTaskMutation.isPending}
            className="remodra-button"
          >
            <Save className="h-4 w-4 mr-2" />
            {addTaskMutation.isPending ? 'Creating...' : 'Create Task'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Edit Task Dialog */}
    <Dialog open={isEditTaskDialogOpen} onOpenChange={setIsEditTaskDialogOpen}>
      <DialogContent className="bg-slate-800 border-slate-600 text-slate-100">
        <DialogHeader>
          <DialogTitle className="text-green-500">Edit Task</DialogTitle>
          <DialogDescription className="text-slate-400">
            Update task details and status
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="editTaskName" className="text-slate-300">Task Name *</Label>
            <Input
              id="editTaskName"
              value={editTaskName}
              onChange={(e) => setEditTaskName(e.target.value)}
              placeholder="e.g., Install Roofing Shingles"
              className="remodra-input mt-2"
            />
          </div>
          <div>
            <Label htmlFor="editTaskDescription" className="text-slate-300">Description (Optional)</Label>
            <Textarea
              id="editTaskDescription"
              value={editTaskDescription}
              onChange={(e) => setEditTaskDescription(e.target.value)}
              placeholder="Brief description of the task..."
              rows={3}
              className="remodra-input mt-2"
            />
          </div>
          <div>
            <Label htmlFor="editTaskStatus" className="text-slate-300">Status</Label>
            <select
              id="editTaskStatus"
              value={editTaskStatus}
              onChange={(e) => setEditTaskStatus(e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-slate-600 bg-slate-700 text-slate-200 mt-2"
            >
              <option value="not_started">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => {
              setIsEditTaskDialogOpen(false);
              setEditingTask(null);
            }}
            className="remodra-button-outline"
          >
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateTask}
            disabled={updateTaskMutation.isPending}
            className="remodra-button"
          >
            <Save className="h-4 w-4 mr-2" />
            {updateTaskMutation.isPending ? 'Updating...' : 'Update Task'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </div>
    </ErrorBoundary>
  );
} 